import axios from 'axios';
import { AWSData, AWSResource, ApiResponse, InfrastructureError } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export class AWSService {
  private static instance: AWSService;
  private cache = new Map<string, { data: any; timestamp: number }>();
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  static getInstance(): AWSService {
    if (!AWSService.instance) {
      AWSService.instance = new AWSService();
    }
    return AWSService.instance;
  }

  private async apiRequest<T>(endpoint: string): Promise<ApiResponse<T>> {
    try {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        console.warn('AWS Service: No auth token - request may fail');
        // Let backend handle auth validation
      }
      
      const url = `${API_BASE_URL}/api/infrastructure/aws${endpoint}`;
      
      console.log(`AWS API Request: ${url}`, { 
        hasToken: !!token, 
        tokenLength: token?.length,
        tokenPrefix: token?.substring(0, 10),
        token: token,  // Full token for debugging
        apiBaseUrl: API_BASE_URL,
        fullUrl: url
      });
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000, // Increased to 30 seconds for S3 buckets
        withCredentials: false // Ensure CORS works properly
      });
      
      console.log(`AWS API Success for ${endpoint}:`, response.status, 'Data received:', !!response.data?.success, 'Items:', response.data?.data?.length);
      return response.data;
    } catch (error: any) {
      console.error(`AWS API Error for ${endpoint}:`, {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw {
        code: error.response?.status || 'NETWORK_ERROR',
        message: error.response?.data?.message || error.message || 'Failed to fetch AWS data',
        service: 'aws',
        timestamp: new Date().toISOString()
      } as InfrastructureError;
    }
  }

  private getCachedData<T>(key: string): T | null {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return cached.data as T;
    }
    return null;
  }

  private setCachedData<T>(key: string, data: T): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  async getEC2Instances(accountId?: string, region?: string): Promise<AWSResource[]> {
    const cacheKey = `ec2-${accountId || 'default'}-${region || 'all'}`;
    const cached = this.getCachedData<AWSResource[]>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (accountId) params.append('accountId', accountId);
    if (region) params.append('region', region);

    const response = await this.apiRequest<AWSResource[]>(`/ec2/instances?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch EC2 instances');
  }

  async getRDSInstances(accountId?: string, region?: string): Promise<AWSResource[]> {
    const cacheKey = `rds-${accountId || 'default'}-${region || 'all'}`;
    const cached = this.getCachedData<AWSResource[]>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (accountId) params.append('accountId', accountId);
    if (region) params.append('region', region);

    const response = await this.apiRequest<AWSResource[]>(`/rds/instances?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch RDS instances');
  }

  async getS3Buckets(accountId?: string): Promise<AWSResource[]> {
    const cacheKey = `s3-${accountId || 'default'}`;
    const cached = this.getCachedData<AWSResource[]>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (accountId) params.append('accountId', accountId);

    const response = await this.apiRequest<AWSResource[]>(`/s3/buckets?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch S3 buckets');
  }

  async getLambdaFunctions(accountId?: string, region?: string): Promise<AWSResource[]> {
    const cacheKey = `lambda-${accountId || 'default'}-${region || 'all'}`;
    const cached = this.getCachedData<AWSResource[]>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (accountId) params.append('accountId', accountId);
    if (region) params.append('region', region);

    const response = await this.apiRequest<AWSResource[]>(`/lambda/functions?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch Lambda functions');
  }

  async getEKSClusters(accountId?: string, region?: string): Promise<AWSResource[]> {
    const cacheKey = `eks-${accountId || 'default'}-${region || 'all'}`;
    const cached = this.getCachedData<AWSResource[]>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (accountId) params.append('accountId', accountId);
    if (region) params.append('region', region);

    const response = await this.apiRequest<AWSResource[]>(`/eks/clusters?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch EKS clusters');
  }

  async getCostData(accountId?: string, timeRange = '7d'): Promise<any> {
    const cacheKey = `cost-${accountId || 'default'}-${timeRange}`;
    const cached = this.getCachedData<any>(cacheKey);
    if (cached) return cached;

    const params = new URLSearchParams();
    if (accountId) params.append('accountId', accountId);
    params.append('timeRange', timeRange);

    const response = await this.apiRequest<any>(`/cost/analysis?${params.toString()}`);
    if (response.success && response.data) {
      this.setCachedData(cacheKey, response.data);
      return response.data;
    }
    throw new Error(response.error || 'Failed to fetch cost data');
  }

  async getAllData(accountId?: string): Promise<AWSData> {
    try {
      console.log('AWS Service: Starting getAllData for accountId:', accountId);
      
      const [
        ec2Instances,
        rdsInstances,
        s3Buckets,
        lambdaFunctions,
        eksClusters,
        costData
      ] = await Promise.allSettled([
        this.getEC2Instances(accountId),
        this.getRDSInstances(accountId),
        this.getS3Buckets(accountId),
        this.getLambdaFunctions(accountId),
        this.getEKSClusters(accountId),
        this.getCostData(accountId, 'last-month')
      ]);

      console.log('AWS Service: Promise.allSettled results:', {
        ec2: ec2Instances.status,
        rds: rdsInstances.status,
        s3: s3Buckets.status,
        lambda: lambdaFunctions.status,
        eks: eksClusters.status,
        cost: costData.status
      });

      // Log individual failures
      if (ec2Instances.status === 'rejected') console.error('AWS EC2 failed:', ec2Instances.reason);
      if (rdsInstances.status === 'rejected') console.error('AWS RDS failed:', rdsInstances.reason);
      if (s3Buckets.status === 'rejected') console.error('AWS S3 failed:', s3Buckets.reason);
      if (lambdaFunctions.status === 'rejected') console.error('AWS Lambda failed:', lambdaFunctions.reason);
      if (eksClusters.status === 'rejected') console.error('AWS EKS failed:', eksClusters.reason);
      if (costData.status === 'rejected') console.error('AWS Cost failed:', costData.reason);

      let ec2Data = ec2Instances.status === 'fulfilled' ? ec2Instances.value : [];
      let rdsData = rdsInstances.status === 'fulfilled' ? rdsInstances.value : [];
      let s3Data = s3Buckets.status === 'fulfilled' ? s3Buckets.value : [];
      let lambdaData = lambdaFunctions.status === 'fulfilled' ? lambdaFunctions.value : [];
      let eksData = eksClusters.status === 'fulfilled' ? eksClusters.value : [];
      const cost = costData.status === 'fulfilled' ? costData.value : { total: 0, byService: {}, trend: 'stable' };

      console.log('AWS Service: Raw data counts:', {
        ec2Count: ec2Data.length,
        rdsCount: rdsData.length,
        s3Count: s3Data.length,
        lambdaCount: lambdaData.length,
        eksCount: eksData.length
      });

      // Show real data counts (even if zero)
      console.log('AWS Service: Real data counts - EC2:', ec2Data.length, 'RDS:', rdsData.length, 'S3:', s3Data.length, 'Lambda:', lambdaData.length, 'EKS:', eksData.length);

      // Collect permission errors from failed services AND from services that returned 0 results
      const missingPermissions: string[] = [];
      const extractPermission = (reason: any) => {
        const msg = reason?.message || String(reason);
        const match = msg.match(/not authorized to perform: ([^\s]+)/);
        if (match) return match[1];
        if (msg.includes('AccessDenied') || msg.includes('not authorized')) {
          const colonParts = msg.split(':');
          return colonParts.pop()?.trim() || msg;
        }
        return null;
      };

      // Check rejected promises (direct failures like S3)
      if (ec2Instances.status === 'rejected') { const p = extractPermission(ec2Instances.reason); if (p) missingPermissions.push(p); }
      if (rdsInstances.status === 'rejected') { const p = extractPermission(rdsInstances.reason); if (p) missingPermissions.push(p); }
      if (s3Buckets.status === 'rejected') { const p = extractPermission(s3Buckets.reason); if (p) missingPermissions.push(p); }
      if (lambdaFunctions.status === 'rejected') { const p = extractPermission(lambdaFunctions.reason); if (p) missingPermissions.push(p); }
      if (eksClusters.status === 'rejected') { const p = extractPermission(eksClusters.reason); if (p) missingPermissions.push(p); }

      // For multi-region services: if 0 results returned, do a quick single-region check
      // to detect if it's a permission issue vs genuinely empty
      if (ec2Data.length === 0 && ec2Instances.status === 'fulfilled') {
        try {
          await this.apiRequest<AWSResource[]>(`/ec2/instances?accountId=${accountId}&region=us-east-1`);
        } catch (e: any) {
          const p = extractPermission(e); if (p) missingPermissions.push(`ec2:DescribeInstances`);
        }
      }
      if (rdsData.length === 0 && rdsInstances.status === 'fulfilled') {
        try {
          await this.apiRequest<AWSResource[]>(`/rds/instances?accountId=${accountId}&region=us-east-1`);
        } catch (e: any) {
          const p = extractPermission(e); if (p) missingPermissions.push(`rds:DescribeDBInstances`);
        }
      }
      if (lambdaData.length === 0 && lambdaFunctions.status === 'fulfilled') {
        try {
          await this.apiRequest<AWSResource[]>(`/lambda/functions?accountId=${accountId}&region=us-east-1`);
        } catch (e: any) {
          const p = extractPermission(e); if (p) missingPermissions.push(`lambda:ListFunctions`);
        }
      }
      if (eksData.length === 0 && eksClusters.status === 'fulfilled') {
        try {
          await this.apiRequest<AWSResource[]>(`/eks/clusters?accountId=${accountId}&region=us-east-1`);
        } catch (e: any) {
          const p = extractPermission(e); if (p) missingPermissions.push(`eks:ListClusters`);
        }
      }

      if (missingPermissions.length > 0) {
        console.warn('AWS Service: Missing permissions detected:', missingPermissions);
      }

      const result = {
        ec2: {
          instances: ec2Data,
          total: ec2Data.length,
          running: ec2Data.filter(i => i.status === 'running').length,
          stopped: ec2Data.filter(i => i.status === 'stopped').length
        },
        rds: {
          instances: rdsData,
          total: rdsData.length,
          available: rdsData.filter(i => i.status === 'available').length,
          maintenance: rdsData.filter(i => i.status === 'maintenance').length
        },
        s3: {
          buckets: s3Data,
          totalSize: this.calculateTotalSize(s3Data),
          totalObjects: this.calculateTotalObjects(s3Data)
        },
        lambda: {
          functions: lambdaData,
          total: lambdaData.length,
          active: lambdaData.filter(f => f.status === 'active' || f.status === 'available').length
        },
        eks: {
          clusters: eksData,
          total: eksData.length,
          active: eksData.filter(c => c.status === 'active').length
        },
        cost,
        missingPermissions
      };

      console.log('AWS Service: Final result structure:', {
        ec2Total: result.ec2.total,
        ec2Running: result.ec2.running,
        rdsTotal: result.rds.total,
        s3Total: result.s3.buckets.length,
        lambdaTotal: result.lambda.total,
        eksTotal: result.eks.total,
        totalCost: result.cost.total
      });

      return result;
    } catch (error) {
      console.error('Error fetching AWS data:', error);
      throw error;
    }
  }

  private calculateTotalSize(buckets: AWSResource[]): string {
    const totalBytes = buckets.reduce((sum, bucket) => {
      return sum + (bucket.metadata?.sizeBytes || 0);
    }, 0);
    
    if (totalBytes < 1024) return `${totalBytes} B`;
    if (totalBytes < 1024 * 1024) return `${(totalBytes / 1024).toFixed(1)} KB`;
    if (totalBytes < 1024 * 1024 * 1024) return `${(totalBytes / (1024 * 1024)).toFixed(1)} MB`;
    if (totalBytes < 1024 * 1024 * 1024 * 1024) return `${(totalBytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    return `${(totalBytes / (1024 * 1024 * 1024 * 1024)).toFixed(1)} TB`;
  }

  private calculateTotalObjects(buckets: AWSResource[]): number {
    return buckets.reduce((sum, bucket) => {
      return sum + (bucket.metadata?.objectCount || 0);
    }, 0);
  }

  clearCache(): void {
    this.cache.clear();
  }
}

export const awsService = AWSService.getInstance();